import { useContext, useEffect, useRef, useState } from "react";
import CustomDataTable from "../components/dataTable";
import OffIcon from '../icons/off';
import OnIcon from '../icons/on';
import CustomModal from "../components/modal";
import { ToastDataContext } from "../context/ToastContextProvider";
import CustomTextInput from "../components/textInput";
import CustomButton from "../components/button";
import { QRCode } from 'react-qrcode-logo';
import { qrQuietZone } from '../utils';
import { UserDataContext } from "../context/UserContextProvider";
import useSWR from "swr";
import { hrChangeEmployeeStateEndpoint, hrCompanyWideLimitStatsEndpoint, hrEmployeeListEndpoint, hrUpdateCompanyWideLimitEndpoint, hrUpdateEmployeeLimitEndpoint, hrGetContractTypesEndpoint } from "../hr_endpoints";
import { PencilIcon, ChevronDownIcon, ChevronUpIcon } from "@heroicons/react/24/outline";
import LimitIcon from "../icons/limit";
import CustomNumberInput from "../components/numberInput";
import { useForm } from "react-hook-form";
import HREmployeeAddModal from "./HREmployeeAddModal";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

// A period is limited only when it carries a real cap. "Not limited" is a
// NULL in the database, but a 0 can still reach us from an older payload or a
// cached response, and 0 stopped being a legal limit when periods became
// switchable — so both mean the same thing, and neither is a cap of zero.
const limitValue = (value) => (Number.isFinite(value) && value > 0 ? value : null)
const hasLimit = (value) => limitValue(value) !== null

export default function HREmployees({ }) {

    const userContext = useContext(UserDataContext);
    const toastContext = useContext(ToastDataContext);
    const { t } = useTranslation();
    const { data: companyLimits, error: companyLimitsError, mutate: mutateCompanyLimits } = useSWR([hrCompanyWideLimitStatsEndpoint])
    const { data: employeeList, error: employeeListError, mutate: mutateEmployeeList } = useSWR([hrEmployeeListEndpoint])
    const { data: contractTypes, error: contractTypesError } = useSWR([hrGetContractTypesEndpoint])

    useEffect(() => {
        if (companyLimitsError) toastContext.error(companyLimitsError.message)
        if (employeeListError) toastContext.error(employeeListError.message)
        if (contractTypesError) toastContext.error(contractTypesError.message)
    }, [companyLimitsError, employeeListError, contractTypesError])

    const navigate = useNavigate()

    // Company limit inline editing state
    const [editingCell, setEditingCell] = useState(null); // { contractTypeId, limitType }
    const [editValue, setEditValue] = useState(0);

    const startEditCell = (contractTypeId, limitType, currentValue) => {
        setEditingCell({ contractTypeId, limitType });
        setEditValue(currentValue || 0);
    };

    const cancelEditCell = () => {
        setEditingCell(null);
        setEditValue(0);
    };

    const isEditingCell = (contractTypeId, limitType) => {
        return editingCell?.contractTypeId === contractTypeId && editingCell?.limitType === limitType;
    };

    // Returns null when that period is not limited, so callers can tell
    // "no cap" apart from a real value.
    const getLimitValue = (contractTypeId, limitType) => {
        const limits = companyLimits?.find(l => l.contract_type_id === contractTypeId);
        if (!limits) return null;
        return limits[limitType] ?? null;
    };

    // The endpoint takes the raw value in the path; "off" clears the limit.
    const saveCompanyLimitValue = async (limitType, contractTypeID, rawValue) => {
        const response = await fetch(hrUpdateCompanyWideLimitEndpoint(limitType, rawValue, contractTypeID), {
            method: "PUT",
            headers: {
                "Authorization": `Bearer ${userContext.user?.token}`
            }
        })

        if (response.status === 401) {
            userContext.setUserData({})
            navigate('/login')
            toastContext.error(t("sessionExpired"));
            return false
        }

        if (!response.ok) {
            toastContext.error(await response.text())
            return false
        }

        toastContext.success(t("employees.limitUpdated"))
        mutateCompanyLimits()
        return true
    };

    const clearCompanyLimit = (limitType, contractTypeID) =>
        saveCompanyLimitValue(limitType, contractTypeID, "off");

    const saveCompanyLimit = async (limitType, contractTypeID) => {
        const value = parseInt(editValue, 10)
        if (!Number.isFinite(value) || value < 1) {
            toastContext.error(t("employees.limitMustBePositive"))
            return
        }
        if (await saveCompanyLimitValue(limitType, contractTypeID, String(value))) {
            cancelEditCell()
        }
    }

    const [qrOpen, setQROpen] = useState(false);
    const uuidAddInfo = userContext.user?.signup_uuid || '';
    const setQRCode = `${import.meta.env.VITE_CORE_ANGAJAT_URL}/register/${uuidAddInfo}`;

    // Track expanded rows for limits in employee table
    const [expandedLimits, setExpandedLimits] = useState({});

    const toggleLimitExpand = (rowId) => {
        setExpandedLimits(prev => ({
            ...prev,
            [rowId]: !prev[rowId]
        }));
    };

    const columns = [
        {
            accessorKey: "name",
            header: t("employees.name"),
        },
        {
            accessorKey: "slots",
            header: t("employees.slots"),
            cell: ({ row }) => (
                <span>
                    <span className="text-accent_green">
                        {t("employees.completedSlots")}: <b>{row.original.completed_slots}</b>
                    </span> <br />
                    <span className="text-tertiary">
                        {t("employees.cancelledSlots")}: <b>{row.original.cancelled_slots}</b>
                    </span>
                </span>
            )
        },
        {
            accessorKey: "limit",
            header: t("employees.limit"),
            cell: ({ row }) => {
                const limits = row.original.limits || [];
                const hasLimits = limits.length > 0 && limits.some(l => hasLimit(l.day_limit) || hasLimit(l.week_limit) || hasLimit(l.month_limit));
                const isExpanded = expandedLimits[row.original.id];

                if (!hasLimits) {
                    return (
                        <span className="text-gray-400 text-sm italic">
                            {t("employees.noLimits") || "Fără limite"}
                        </span>
                    );
                }

                const configuredCount = limits.filter(l => hasLimit(l.day_limit) || hasLimit(l.week_limit) || hasLimit(l.month_limit)).length;

                return (
                    <div className="min-w-[180px]">
                        {/* Compact Summary - Click to expand */}
                        <button
                            onClick={() => toggleLimitExpand(row.original.id)}
                            className="flex items-center gap-1 text-sm text-primary hover:text-primary-dark transition-colors"
                        >
                            <span className="font-medium">
                                {configuredCount} {configuredCount === 1 ? t("employees.contractTypeSingular") || 'tip contract' : t("employees.contractTypePlural") || 'tipuri contract'}
                            </span>
                            {isExpanded ? (
                                <ChevronUpIcon className="w-4 h-4" />
                            ) : (
                                <ChevronDownIcon className="w-4 h-4" />
                            )}
                        </button>

                        {/* Expanded Details */}
                        {isExpanded && (
                            <div className="mt-2 space-y-2">
                                {limits.map((limit, idx) => {
                                    const hasAnyLimit = hasLimit(limit.day_limit) || hasLimit(limit.week_limit) || hasLimit(limit.month_limit);
                                    if (!hasAnyLimit) return null;

                                    return (
                                        <div key={idx} className="bg-base-200/50 rounded-lg p-2 text-xs">
                                            <div className="font-semibold text-sm mb-1 text-base-content">
                                                {limit.contract_type_name}
                                            </div>
                                            <div className="flex flex-wrap gap-x-3 gap-y-1">
                                                {hasLimit(limit.day_limit) && (
                                                    <span className="text-accent_green">
                                                        {t("employees.dayShort") || "Z"}: <b>{limit.day_limit}</b>
                                                    </span>
                                                )}
                                                {hasLimit(limit.week_limit) && (
                                                    <span className="text-primary">
                                                        {t("employees.weekShort") || "S"}: <b>{limit.week_limit}</b>
                                                    </span>
                                                )}
                                                {hasLimit(limit.month_limit) && (
                                                    <span className="text-primary-dark">
                                                        {t("employees.monthShort") || "L"}: <b>{limit.month_limit}</b>
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                );
            }
        },
        {
            accessorKey: "status",
            header: t("employees.status"),
            cell: ({ row }) => (
                <span
                    className={
                        row.original.status
                            ? "text-green-500"
                            : "text-red-500"
                    }
                >
                    {row.original.status ? t("employees.active") : t("employees.inactive")}
                </span>
            ),
        },
        {
            accessorKey: "actions",
            header: t("employees.actions"),
            cell: ({ row }) => (
                <div className="flex flex-row gap-2">
                    <span onClick={() => onOpenLimit(row.original)}>
                        <LimitIcon className="size-6 cursor-pointer" />
                    </span>
                    {row.original.status && <span onClick={() => { onUserStateChange(row.original, true) }}><OnIcon className="size-7 cursor-pointer fill-accent_green" /></span>}
                    {!row.original.status && <span onClick={() => { onUserStateChange(row.original, false) }}><OffIcon className="size-7 cursor-pointer fill-tertiary" /></span>}
                </div>
            )
        }
    ];

    const onUserStateChange = async (activeUser, disable) => {
        toastContext.showConfirmationDialog(disable ? t("employees.deactivateUser", { name: activeUser.name }) : t("employees.activateUser", { name: activeUser.name }), t("employees.changeUserStateTitle", { name: activeUser.name })).then(async (value) => {
            if (value) {
                const response = await fetch(hrChangeEmployeeStateEndpoint(activeUser.id), {
                    method: 'PUT',
                    body: JSON.stringify({ status: disable ? '0' : '1' }),
                    headers: {
                        Authorization: `Bearer ${userContext.user?.token}`,
                        "Content-Type": "application/json"
                    }
                })
                if (response.status === 401) {
                    userContext.setUserData({})
                    navigate('/login')
                    toastContext.error(t("sessionExpired"));
                }
                if (!response.ok) {
                    toastContext.error(await response.text());
                } else {
                    toastContext.success(t("employees.employeeUpdated"));
                    mutateEmployeeList()
                }
            }
        })
    }

    const [pagination, setPagination] = useState({
        pageIndex: 0,
        pageSize: 10,
    });

    const [sorting, setSorting] = useState([
        {
            id: "name",
            desc: false
        },
    ])

    const [filterByName, setFilterByName] = useState("")
    const [filteredData, setFilteredData] = useState([])

    useEffect(() => {
        if (employeeList) {
            setFilteredData(employeeList.filter(t => t.name.toLowerCase().includes(filterByName.toLowerCase())))
        }
    }, [employeeList, filterByName]);

    const formRef = useRef(null);

    const [open, setOpen] = useState(false);
    const [activeTab, setActiveTab] = useState(0);

    const onCancel = () => {
        setOpen(false)
        reset()
        setActiveTab(0)
    }

    const onSubmit = async (data) => {
        const limitUpdates = [];
        contractTypes?.forEach(ct => {
            const dayKey = `max_per_day_${ct.id}`;
            const weekKey = `max_per_week_${ct.id}`;
            const monthKey = `max_per_month_${ct.id}`;
            // A period that is switched off is sent as null: the backend stores
            // NULL for "not limited". 0 is no longer a way to express anything.
            //
            // A switched-on period returns whatever was typed, NaN included, so
            // an empty box is reported below rather than quietly turning the
            // limit off behind the back of the person who just switched it on.
            const read = (key, onKey) => {
                if (!data[onKey]) return null
                return parseInt(data[key], 10)
            }

            limitUpdates.push({
                contract_type_id: ct.id,
                max_per_day: read(dayKey, `${dayKey}_on`),
                max_per_week: read(weekKey, `${weekKey}_on`),
                max_per_month: read(monthKey, `${monthKey}_on`)
            });
        });

        for (const limitUpdate of limitUpdates) {
            for (const field of ["max_per_day", "max_per_week", "max_per_month"]) {
                const value = limitUpdate[field]
                // null is "switched off" and never blocks a save, whatever was
                // left in the box before it was unchecked.
                if (value === null) continue
                if (!Number.isFinite(value) || value < 1) {
                    toastContext.error(t("employees.limitMustBePositive"))
                    return
                }
            }
        }

        for (const limitUpdate of limitUpdates) {
            const response = await fetch(hrUpdateEmployeeLimitEndpoint(data.id), {
                method: "PUT",
                headers: {
                    "Authorization": `Bearer ${userContext.user?.token}`,
                    "Content-Type": "application/json"
                },
                body: JSON.stringify(limitUpdate)
            })
            if (response.status === 401) {
                userContext.setUserData({})
                navigate('/login')
                toastContext.error(t("sessionExpired"));
                return;
            }
            if (!response.ok) {
                toastContext.error(await response.text())
                return;
            }
        }
        toastContext.success(t("employees.limitUpdated"))
        mutateEmployeeList()
        onCancel()
    }

    const { register, handleSubmit, watch, reset, setValue } = useForm();

    // The checkbox owns the field. Switching a limit on puts a number in the
    // box so it is obvious one is expected; switching it off empties the box,
    // so nothing stale is left behind to be validated or saved.
    const onToggleLimit = (key) => (event) => {
        setValue(key, event.target.checked ? 0 : "")
    }

    const onOpenLimit = (employee) => {
        const formData = { id: employee.id };
        contractTypes?.forEach(ct => {
            const saved = employee.limits?.find(l => l.contract_type_id === ct.id);
            for (const [key, field] of [
                [`max_per_day_${ct.id}`, "day_limit"],
                [`max_per_week_${ct.id}`, "week_limit"],
                [`max_per_month_${ct.id}`, "month_limit"],
            ]) {
                const value = limitValue(saved ? saved[field] : null);
                formData[`${key}_on`] = value !== null;
                formData[key] = value ?? "";
            }
        });
        reset(formData);
        setActiveTab(0);
        setOpen(true);
    }

    return (
        <div>
            {/* Company-Wide Limits Section - Compact Table */}
            <section className="mb-8">
                <h2 className="text-2xl font-bold mb-4">{t("employees.title")}</h2>

                <div className="card bg-base-100 shadow-xl overflow-hidden">
                    <div className="overflow-x-auto">
                        <table className="table table-zebra w-full">
                            <thead>
                                <tr className="bg-base-200">
                                    <th className="font-semibold">{t("employees.contractType") || "Tip Contract"}</th>
                                    <th className="text-center font-semibold">{t("employees.dailyLimit")}</th>
                                    <th className="text-center font-semibold">{t("employees.weeklyLimit")}</th>
                                    <th className="text-center font-semibold">{t("employees.monthlyLimit")}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {contractTypes && contractTypes.map((contractType) => (
                                    <tr key={contractType.id}>
                                        <td className="font-medium">{contractType.name}</td>

                                        {/* Daily Limit Cell */}
                                        <td className="text-center">
                                            {isEditingCell(contractType.id, 'day') ? (
                                                <div className="flex items-center justify-center gap-2">
                                                    <input
                                                        type="number"
                                                        className="input input-bordered input-sm w-20 text-center"
                                                        min={0}
                                                        value={editValue}
                                                        onChange={(e) => setEditValue(e.target.value)}
                                                        autoFocus
                                                        onKeyDown={(e) => {
                                                            if (e.key === 'Enter') saveCompanyLimit('max_per_day', contractType.id);
                                                            if (e.key === 'Escape') cancelEditCell();
                                                        }}
                                                    />
                                                    <button
                                                        className="btn btn-primary btn-xs"
                                                        onClick={() => saveCompanyLimit('max_per_day', contractType.id)}
                                                    >
                                                        ✓
                                                    </button>
                                                    <button
                                                        className="btn btn-ghost btn-xs"
                                                        onClick={cancelEditCell}
                                                    >
                                                        ✕
                                                    </button>
                                                </div>
                                            ) : (
                                                <div
                                                    className="inline-flex items-center gap-2 cursor-pointer hover:bg-base-200 px-3 py-1 rounded transition-colors group"
                                                    onClick={() => startEditCell(contractType.id, 'day', getLimitValue(contractType.id, 'day'))}
                                                >
                                                    <span className="font-bold text-accent_green">
                                                        {limitValue(getLimitValue(contractType.id, 'day')) ?? t("employees.noLimit")}
                                                    </span>
                                                    {getLimitValue(contractType.id, 'day') != null && (
                                                        <button
                                                            type="button"
                                                            className="text-xs text-gray-400 hover:text-tertiary underline"
                                                            onClick={(e) => { e.stopPropagation(); clearCompanyLimit('max_per_day', contractType.id); }}
                                                        >
                                                            {t("employees.turnOff")}
                                                        </button>
                                                    )}
                                                    <PencilIcon className="w-4 h-4 text-gray-400 opacity-0 group-hover:opacity-100 transition-opacity" />
                                                </div>
                                            )}
                                        </td>

                                        {/* Weekly Limit Cell */}
                                        <td className="text-center">
                                            {isEditingCell(contractType.id, 'week') ? (
                                                <div className="flex items-center justify-center gap-2">
                                                    <input
                                                        type="number"
                                                        className="input input-bordered input-sm w-20 text-center"
                                                        min={0}
                                                        value={editValue}
                                                        onChange={(e) => setEditValue(e.target.value)}
                                                        autoFocus
                                                        onKeyDown={(e) => {
                                                            if (e.key === 'Enter') saveCompanyLimit('max_per_week', contractType.id);
                                                            if (e.key === 'Escape') cancelEditCell();
                                                        }}
                                                    />
                                                    <button
                                                        className="btn btn-primary btn-xs"
                                                        onClick={() => saveCompanyLimit('max_per_week', contractType.id)}
                                                    >
                                                        ✓
                                                    </button>
                                                    <button
                                                        className="btn btn-ghost btn-xs"
                                                        onClick={cancelEditCell}
                                                    >
                                                        ✕
                                                    </button>
                                                </div>
                                            ) : (
                                                <div
                                                    className="inline-flex items-center gap-2 cursor-pointer hover:bg-base-200 px-3 py-1 rounded transition-colors group"
                                                    onClick={() => startEditCell(contractType.id, 'week', getLimitValue(contractType.id, 'week'))}
                                                >
                                                    <span className="font-bold text-primary">
                                                        {limitValue(getLimitValue(contractType.id, 'week')) ?? t("employees.noLimit")}
                                                    </span>
                                                    {getLimitValue(contractType.id, 'week') != null && (
                                                        <button
                                                            type="button"
                                                            className="text-xs text-gray-400 hover:text-tertiary underline"
                                                            onClick={(e) => { e.stopPropagation(); clearCompanyLimit('max_per_week', contractType.id); }}
                                                        >
                                                            {t("employees.turnOff")}
                                                        </button>
                                                    )}
                                                    <PencilIcon className="w-4 h-4 text-gray-400 opacity-0 group-hover:opacity-100 transition-opacity" />
                                                </div>
                                            )}
                                        </td>

                                        {/* Monthly Limit Cell */}
                                        <td className="text-center">
                                            {isEditingCell(contractType.id, 'month') ? (
                                                <div className="flex items-center justify-center gap-2">
                                                    <input
                                                        type="number"
                                                        className="input input-bordered input-sm w-20 text-center"
                                                        min={0}
                                                        value={editValue}
                                                        onChange={(e) => setEditValue(e.target.value)}
                                                        autoFocus
                                                        onKeyDown={(e) => {
                                                            if (e.key === 'Enter') saveCompanyLimit('max_per_month', contractType.id);
                                                            if (e.key === 'Escape') cancelEditCell();
                                                        }}
                                                    />
                                                    <button
                                                        className="btn btn-primary btn-xs"
                                                        onClick={() => saveCompanyLimit('max_per_month', contractType.id)}
                                                    >
                                                        ✓
                                                    </button>
                                                    <button
                                                        className="btn btn-ghost btn-xs"
                                                        onClick={cancelEditCell}
                                                    >
                                                        ✕
                                                    </button>
                                                </div>
                                            ) : (
                                                <div
                                                    className="inline-flex items-center gap-2 cursor-pointer hover:bg-base-200 px-3 py-1 rounded transition-colors group"
                                                    onClick={() => startEditCell(contractType.id, 'month', getLimitValue(contractType.id, 'month'))}
                                                >
                                                    <span className="font-bold text-primary-dark">
                                                        {limitValue(getLimitValue(contractType.id, 'month')) ?? t("employees.noLimit")}
                                                    </span>
                                                    {getLimitValue(contractType.id, 'month') != null && (
                                                        <button
                                                            type="button"
                                                            className="text-xs text-gray-400 hover:text-tertiary underline"
                                                            onClick={(e) => { e.stopPropagation(); clearCompanyLimit('max_per_month', contractType.id); }}
                                                        >
                                                            {t("employees.turnOff")}
                                                        </button>
                                                    )}
                                                    <PencilIcon className="w-4 h-4 text-gray-400 opacity-0 group-hover:opacity-100 transition-opacity" />
                                                </div>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            </section>

            <div className="flex flex-col sm:flex-row gap-5 mb-4 ml-4">
                <CustomButton onClick={() => { setQROpen(true) }}>{t("employees.addEmployee")}</CustomButton>
                <CustomTextInput placeholder={t("employees.search")} onChange={(e) => setFilterByName(e.target.value)} value={filterByName} />
            </div>

            <CustomDataTable
                data={filteredData}
                columns={columns}
                pagination={pagination}
                sorting={sorting}
                onSortingChange={setSorting}
                onPaginationChange={setPagination}
            />

            <HREmployeeAddModal QRComponent={() => <QRCode value={setQRCode} size={256} quietZone={qrQuietZone(256)} />} open={qrOpen} setOpen={setQROpen} forceMutate={mutateEmployeeList} />

            {/* Employee Limit Edit Modal with Tabbed Interface */}
            <CustomModal
                open={open}
                setOpen={setOpen}
                title={t("employees.modifyEmployeeLimit")}
                cancelButton={t("employees.cancel")}
                confirmButton={t("employees.save")}
                onConfirm={() => {
                    formRef.current?.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }));
                }}
                onCancel={onCancel}
                noOutsideClick={true}
                withIcon={false}
            >
                <form ref={formRef} onSubmit={handleSubmit(onSubmit)}>
                    {/* Contract types as a segmented control that scrolls on one
                        line rather than reflowing into a grid, so adding a type
                        never pushes the fields below out of view. */}
                    {contractTypes && contractTypes.length > 1 && (
                        <div className="flex flex-wrap gap-1 p-1 mb-5 rounded-xl bg-neutral-100">
                            {contractTypes.map((ct, idx) => (
                                <button
                                    key={ct.id}
                                    type="button"
                                    className={`whitespace-nowrap rounded-lg px-4 py-2 font-text font-medium transition-colors ${activeTab === idx
                                        ? "bg-primary text-white shadow-sm"
                                        : "text-neutral-500 hover:text-neutral-1100"
                                        }`}
                                    onClick={() => setActiveTab(idx)}
                                    title={ct.name}
                                >
                                    {ct.name}
                                </button>
                            ))}
                        </div>
                    )}

                    {contractTypes?.map((contractType, idx) => (
                        <div
                            key={contractType.id}
                            className={activeTab === idx ? "block" : "hidden"}
                        >
                            {contractTypes.length === 1 && (
                                <h4 className="font-subtitle text-neutral-1100 mb-3">{contractType.name}</h4>
                            )}

                            {/* One row per period. A label free to wrap no longer
                                drags its field out of line with the other two, which
                                is what the three-column grid did as soon as "Limita
                                pe săptămână" needed a second line. */}
                            <div className="rounded-xl border border-neutral-border divide-y divide-neutral-border overflow-hidden">
                                {[
                                    [`max_per_day_${contractType.id}`, t("employees.dayLimit")],
                                    [`max_per_week_${contractType.id}`, t("employees.weekLimit")],
                                    [`max_per_month_${contractType.id}`, t("employees.monthLimit")],
                                ].map(([key, label]) => {
                                    const enabled = watch(`${key}_on`)
                                    return (
                                        <div key={key} className="flex items-center justify-between gap-3 px-4 py-3 transition-colors hover:bg-neutral-100/70">
                                            <label htmlFor={`${key}_on`} className="flex min-w-0 items-center gap-3 cursor-pointer">
                                                <input
                                                    id={`${key}_on`}
                                                    type="checkbox"
                                                    className="h-4 w-4 shrink-0 cursor-pointer rounded border-neutral-border accent-primary"
                                                    {...register(`${key}_on`, { onChange: onToggleLimit(key) })}
                                                />
                                                <span className={`font-text font-medium ${enabled ? "text-neutral-1100" : "text-neutral-400"}`}>
                                                    {label}
                                                </span>
                                            </label>
                                            <CustomNumberInput
                                                min={1}
                                                step={1}
                                                placeholder="—"
                                                disabled={!enabled}
                                                register={register(key)}
                                                className="w-24 shrink-0 py-1.5 text-center !text-neutral-1100 disabled:cursor-not-allowed disabled:bg-neutral-100 disabled:!text-neutral-400"
                                            />
                                        </div>
                                    )
                                })}
                            </div>

                            <p className="mt-3 font-text text-neutral-500">
                                {t("employees.limitHint")}
                            </p>
                        </div>
                    ))}
                </form>
            </CustomModal>
        </div>
    )

}